export const QUOTE_ID_KEY = "quote_id"
export const USER_ID_KEY = "user_id"

export function storeQuoteId(quoteId: string | undefined) {
  if (!quoteId || typeof quoteId !== "string") {
    return
  }
  if (typeof window === "undefined") {
    return
  }
  sessionStorage.setItem(QUOTE_ID_KEY, quoteId)
}

export function getQuoteIdFromStorage(): string | undefined | null {
  if (typeof window === "undefined") {
    return null
  }
  return sessionStorage.getItem(QUOTE_ID_KEY)
}

export function removeQuoteIdFromStorage() {
  if (typeof window === "undefined") {
    return
  }
  void sessionStorage.removeItem(QUOTE_ID_KEY)
}

export function storeUserId(userId: string | undefined) {
  if (!userId || typeof userId !== "string") {
    return
  }
  if (typeof window === "undefined") {
    return
  }
  localStorage.setItem(USER_ID_KEY, userId)
}

export function getUserIdFromStorage(): string | null {
  if (typeof window === "undefined") {
    return null
  }
  return localStorage.getItem(USER_ID_KEY)
}

const LIABILITY_STATE_KEY = "liability_state"

export type StoredPetLiability = {
  selected: boolean
  price: number | null
  previouslyInsured: boolean | null
  claimsCount: string | null
}

/** RC selection is per-dog: keyed by the pet's `idx`. Not part of the backend quote. */
export type StoredLiabilityState = Record<number, StoredPetLiability>

export function storePetLiabilityState(idx: number, petState: StoredPetLiability) {
  if (typeof window === "undefined") {
    return
  }
  const all = getLiabilityState() ?? {}
  all[idx] = petState
  sessionStorage.setItem(LIABILITY_STATE_KEY, JSON.stringify(all))
}

export function getLiabilityState(): StoredLiabilityState | null {
  if (typeof window === "undefined") {
    return null
  }
  const raw = sessionStorage.getItem(LIABILITY_STATE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredLiabilityState
  } catch {
    return null
  }
}

/** True when any stored pet has the RC add-on selected. */
export function isAnyStoredLiabilitySelected(state: StoredLiabilityState | null): boolean {
  return Boolean(state) && Object.values(state as StoredLiabilityState).some((p) => p.selected)
}

const SMQ_POPIN_DISMISSED_KEY = "smq_popin_dismissed"

/** True once the user has closed the SMQ v2 pop-in during this browser session. */
export function isSmqPopinDismissed(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  try {
    return sessionStorage.getItem(SMQ_POPIN_DISMISSED_KEY) === "true"
  } catch {
    return false
  }
}

export function markSmqPopinDismissed() {
  if (typeof window === "undefined") {
    return
  }
  try {
    sessionStorage.setItem(SMQ_POPIN_DISMISSED_KEY, "true")
  } catch {
    // Storage unavailable (private mode, quota): best-effort, the pop-in
    // will simply auto-open again rather than crash.
  }
}

export type SmqVariant = "v1" | "v2"

const SMQ_VARIANT_OVERRIDE_KEY = "smq_variant_override"

/**
 * QA override for the "Send my quote" entry point, set via ?smq_variant=.
 * Session-scoped so it survives in-funnel navigation; wins over the backend
 * assignment (see useSmqVariant).
 */
export function getSmqVariantOverride(): SmqVariant | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    const raw = sessionStorage.getItem(SMQ_VARIANT_OVERRIDE_KEY)
    return raw === "v1" || raw === "v2" ? raw : null
  } catch {
    return null
  }
}

export function storeSmqVariantOverride(variant: SmqVariant) {
  if (typeof window === "undefined") {
    return
  }
  try {
    sessionStorage.setItem(SMQ_VARIANT_OVERRIDE_KEY, variant)
  } catch {
    // Storage unavailable: the override only lasts for the current page.
  }
}

const PREPACKAGE_LOADER_SEEN_KEY = "prepackage_loader_seen"

/**
 * Tracks the pets for which the /prepackages pricing loader has already played,
 * scoped to the active quote. Keyed by `quoteId` so a new quote resets the state.
 */
type PrepackageLoaderSeen = {
  quoteId: string
  petIds: string[]
}

function getPrepackageLoaderSeen(): PrepackageLoaderSeen | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    const raw = sessionStorage.getItem(PREPACKAGE_LOADER_SEEN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PrepackageLoaderSeen>
    if (typeof parsed?.quoteId !== "string" || !Array.isArray(parsed.petIds)) {
      return null
    }
    return { quoteId: parsed.quoteId, petIds: parsed.petIds }
  } catch {
    return null
  }
}

/** True when the loader has already played for this pet in the current quote. */
export function hasSeenPrepackageLoader(quoteId: string, petId: string): boolean {
  if (!quoteId || !petId) return false
  const seen = getPrepackageLoaderSeen()
  if (!seen || seen.quoteId !== quoteId) return false
  return seen.petIds.includes(petId)
}

/** Marks the loader as played for this pet. A different quoteId resets the state. */
export function markPrepackageLoaderSeen(quoteId: string, petId: string) {
  if (typeof window === "undefined" || !quoteId || !petId) {
    return
  }
  const seen = getPrepackageLoaderSeen()
  const next: PrepackageLoaderSeen =
    seen && seen.quoteId === quoteId
      ? { quoteId, petIds: seen.petIds.includes(petId) ? seen.petIds : [...seen.petIds, petId] }
      : { quoteId, petIds: [petId] }
  try {
    sessionStorage.setItem(PREPACKAGE_LOADER_SEEN_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable (private mode, quota): best-effort, the loader will
    // simply replay next time rather than crash.
  }
}
